import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
  HttpStatusCode,
} from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { ClientService } from '@dss-libs/shared/data-access/clients';
import { SnackbarService } from '@dss-libs/shared/utility/common-services';
import { EventTypeEnum, IError, SubscriptionStatus } from '@dss-libs/shared/type/common-types';
import { catchError, filter, Observable, take, throwError } from 'rxjs';

export const errorInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const injector = inject(Injector);

  return next(request).pipe(
    catchError((err: HttpErrorResponse) => {
      const snackbar = injector.get(SnackbarService);
      const router = injector.get(Router);

      switch (err.status) {
        case HttpStatusCode.Unauthorized:
          router.navigate(['login'], { queryParamsHandling: 'preserve' });
          break;

        case HttpStatusCode.PaymentRequired: {
          const clientService = injector.get(ClientService);
          const clientDataResource = clientService.clientDataResource;
          toObservable(clientDataResource.status, { injector })
            .pipe(
              filter((status) => status === 'resolved' || status === 'error'),
              take(1)
            )
            .subscribe(() => {
              if (!clientDataResource.hasValue()) return;
              const client = clientDataResource.value();
              if (!client.subscriptionId || client.subscriptionStatus === SubscriptionStatus.Cancelled) {
                clientService.showSubscriptionModal.set(true);
              }
            });
          break;
        }

        case HttpStatusCode.BadRequest:
          for (const fieldError of err.error.validationErrors) {
            for (const msg of fieldError.errors) {
              snackbar.showToaster(EventTypeEnum.ERROR, msg);
            }
          }
          break;

        case HttpStatusCode.Forbidden:
          snackbar.showToaster(EventTypeEnum.ERROR, 'ERRORS.FORBIDDEN');
          break;

        case HttpStatusCode.NotFound:
          snackbar.showToaster(EventTypeEnum.ERROR, 'ERRORS.NOT_FOUND');
          break;

        case HttpStatusCode.InternalServerError:
          snackbar.showToaster(EventTypeEnum.ERROR, 'ERRORS.SERVER_ERROR');
          break;

        default:
          snackbar.showToaster(EventTypeEnum.ERROR, 'ERRORS.UNKNOWN_ERROR');
          break;
      }
      return throwError(() => err as IError);
    })
  );
};
